import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Can } from "@/lib/authorization";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BaseNavigationBar, SettingsBackButton } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { EmailChangeDialog } from "./components/email-change-dialog";
import { LinkUserDialog } from "./components/link-user-dialog";
import { SetupAccountDialog } from "./components/setup-account-dialog";
import { TeamMemberDeleteDialog } from "./components/team-member-delete-dialog";
import { TeamMemberForm } from "./components/team-member-form";
import { TeamMemberList } from "./components/team-member-list";
import { WorkerServiceRestrictionDialog } from "./components/worker-service-restriction-dialog";
import { useTeamMembers } from "./hooks";
import { useTeamManagementUIStore } from "./store/use-team-management-ui-store";

export function TeamManagementSettingsPage() {
  const { t } = useTranslation("settings");

  const {
    activeTab,
    setActiveTab,
    isCreateDialogOpen,
    isEditDialogOpen,
    isDeleteDialogOpen,
    isEmailChangeDialogOpen,
    isLinkUserDialogOpen,
    isSetupAccountDialogOpen,
    isServiceRestrictionDialogOpen,
    emailChangeMemberId,
    linkUserMemberId,
    setupAccountMemberId,
    serviceRestrictionMemberId,
    openCreateDialog,
    closeEmailChangeDialog,
    closeLinkUserDialog,
    closeSetupAccountDialog,
    closeServiceRestrictionDialog,
  } = useTeamManagementUIStore();

  const { error, teamMembers } = useTeamMembers();

  // Find the members for dialogs
  const emailChangeMember = teamMembers.find((m) => m.id === emailChangeMemberId);
  const linkUserMember = teamMembers.find((m) => m.id === linkUserMemberId);
  const setupAccountMember = teamMembers.find((m) => m.id === setupAccountMemberId);
  const serviceRestrictionMember = teamMembers.find((m) => m.id === serviceRestrictionMemberId);

  // Get the currently active invitation from each member's worker.invitations array
  const currentEmailChangeInvitation = emailChangeMember?.worker?.invitations?.[0];

  return (
    <>
      <BaseNavigationBar
        title={t("sections.teamManagement.title")}
        subtitle={t("sections.teamManagement.description")}
        leadElement={<SettingsBackButton key="back-button" />}
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <Can I="create" a="Worker" key="create-button">
            <Button onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              {t("teamManagement.addMember")}
            </Button>
          </Can>,
        ]}
      />

      <PageContent>
        <div className="space-y-6">
          <ErrorAlert error={error} />

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as "active" | "inactive")}
          >
            <TabsList className="mb-2">
              <TabsTrigger value="active">{t("teamManagement.active")}</TabsTrigger>
              <TabsTrigger value="inactive">{t("teamManagement.inactive")}</TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-4">
              <TeamMemberList statusFilter="active" />
            </TabsContent>

            <TabsContent value="inactive" className="space-y-4">
              <TeamMemberList statusFilter="inactive" />
            </TabsContent>
          </Tabs>
        </div>
      </PageContent>

      {/* Dialogs */}
      {isCreateDialogOpen && <TeamMemberForm />}
      {isEditDialogOpen && <TeamMemberForm />}
      {isDeleteDialogOpen && <TeamMemberDeleteDialog />}
      {isEmailChangeDialogOpen && emailChangeMember && (
        <EmailChangeDialog
          teamMember={emailChangeMember}
          currentEmail={emailChangeMember.worker?.user?.email}
          currentInvitation={currentEmailChangeInvitation}
          isOpen={isEmailChangeDialogOpen}
          onClose={closeEmailChangeDialog}
        />
      )}
      {isLinkUserDialogOpen && linkUserMember && (
        <LinkUserDialog
          teamMember={linkUserMember}
          isOpen={isLinkUserDialogOpen}
          onClose={closeLinkUserDialog}
          onLinkUser={async (_workerId, _userId) => {}}
          isLoading={false}
        />
      )}
      {isSetupAccountDialogOpen && setupAccountMember && (
        <SetupAccountDialog
          worker={setupAccountMember}
          isOpen={isSetupAccountDialogOpen}
          onClose={closeSetupAccountDialog}
        />
      )}
      {isServiceRestrictionDialogOpen && serviceRestrictionMember && (
        <WorkerServiceRestrictionDialog
          member={serviceRestrictionMember}
          isOpen={isServiceRestrictionDialogOpen}
          onClose={closeServiceRestrictionDialog}
        />
      )}
    </>
  );
}
